import React, { useState } from 'react';
import { AlertTriangle, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import type { PlanAiIssue, PlanAiProposalResult } from './types';

export const AI_TEXT_MIN = 3;
export const AI_TEXT_MAX = 1000;
const COUNTER_FROM = 800;

const PLACEHOLDER = [
  'Np.:',
  'Kowalski nie może we wtorek, bo ma lekarza.',
  'Nowak w tym tygodniu bez nocek.',
  'W środę rano na hartowni wystarczą 2 osoby.',
].join('\n');

interface PlanAiBoxProps {
  /** Disables the box while another plan action runs. */
  disabled: boolean;
  /** Resolves with the result, or null when the request failed (error already shown). */
  onPropose: (text: string) => Promise<PlanAiProposalResult | null>;
}

/** Free-text box: the AI turns sentences into exception proposals to confirm. */
export const PlanAiBox: React.FC<PlanAiBoxProps> = ({ disabled, onPropose }) => {
  const [text, setText] = useState('');
  const [pending, setPending] = useState(false);
  const [issues, setIssues] = useState<PlanAiIssue[]>([]);

  const length = text.trim().length;
  const tooShort = length > 0 && length < AI_TEXT_MIN;
  const tooLong = length > AI_TEXT_MAX;
  const error = tooShort
    ? `Wpisz co najmniej ${AI_TEXT_MIN} znaki.`
    : tooLong
    ? `Tekst może mieć najwyżej ${AI_TEXT_MAX} znaków (teraz: ${length}).`
    : '';
  const canSubmit = !pending && !disabled && length >= AI_TEXT_MIN && !tooLong;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setPending(true);
    setIssues([]);
    try {
      const result = await onPropose(text.trim());
      if (!result) return; // error shown by the caller; keep the text
      setIssues(result.issues);
      if (result.issues.length === 0) setText('');
    } finally {
      setPending(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-2 rounded-md border border-violet-200 bg-violet-50/50 p-4 dark:border-violet-900 dark:bg-violet-950/20"
      data-testid="plan-ai-box"
    >
      <Label htmlFor="plan-ai-text" className="flex items-center gap-1.5">
        <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-300" />
        Opisz wyjątki zwykłym tekstem
      </Label>
      <Textarea
        id="plan-ai-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder={PLACEHOLDER}
        disabled={pending || disabled}
        aria-invalid={!!error}
        className={cn('bg-background', error && 'border-red-500 focus-visible:ring-red-500')}
      />
      <div className="flex flex-wrap items-start justify-between gap-2 text-xs">
        <span className="text-muted-foreground">
          Nic się nie zmieni, dopóki nie zatwierdzisz propozycji i nie ułożysz planu ponownie. Wpisany tekst jest
          wysyłany do zewnętrznej usługi AI.
        </span>
        {length >= COUNTER_FROM && (
          <span className={cn('text-muted-foreground', tooLong && 'text-red-600 dark:text-red-400')} data-testid="plan-ai-counter">
            {length}/{AI_TEXT_MAX}
          </span>
        )}
      </div>
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" disabled={!canSubmit} data-testid="plan-ai-submit">
          {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
          {pending ? 'Czytam tekst...' : 'Przygotuj propozycje'}
        </Button>
        {pending && (
          <span className="text-sm text-muted-foreground" role="status" data-testid="plan-ai-pending">
            AI analizuje tekst — to może potrwać kilka sekund.
          </span>
        )}
      </div>

      {issues.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30" data-testid="plan-ai-issues">
          <div className="border-b border-amber-200 px-3 py-2 text-sm font-medium text-amber-900 dark:border-amber-900 dark:text-amber-100">
            Tego nie udało się zamienić na wyjątki — popraw tekst i wyślij ponownie:
          </div>
          <ul className="divide-y divide-amber-200 text-sm dark:divide-amber-900">
            {issues.map((issue, i) => (
              <li key={i} className="flex items-start gap-2 px-3 py-2 text-amber-900 dark:text-amber-100">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {issue.sourceText && <span className="italic">„{issue.sourceText}” — </span>}
                  {issue.message}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>
  );
};
